import React, {CSSProperties, useCallback, useEffect, useMemo, useRef} from 'react';
import {Theme} from '@taopix/taopix-design-system';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';
import classNames from 'classnames';
import {AIMode, AssistantType} from '@taopix/taopix-core';
import {useExperience} from '../Experience/ExperienceProvider';

export type ToolPaletteProps = {
  isOpen: boolean;
  children?: any;
  openSectionHeight?: number;
  toolPaletteStyle?: 'docked' | 'floating';
};

const ToolPalette = ({isOpen, children, toolPaletteStyle = 'docked'}: ToolPaletteProps) => {
  const ui = useUiState();
  const {experience, getAIMode} = useExperience();
  const aiMode = getAIMode();
  const defaultFocusDelay = 300;
  const focusDelay = useRef(defaultFocusDelay);
  const toolPaletteRef = useRef<HTMLDivElement>(null);

  // Function to get all the buttons with data-palette-button attributes from within the tool palette
  const paletteButtons = useMemo((): HTMLElement[] => {
    const buttons = document.querySelectorAll('#toolpalette [data-palette-button]');
    return Array.from(buttons) as HTMLElement[];
  }, [children]);

  // Function to handle navigating up and down the palette buttons
  const handleKeydown: EventListener = event => {
    const key = (event as KeyboardEvent).key;
    const shiftKey = (event as KeyboardEvent).shiftKey;

    if (['ArrowDown', 'ArrowUp'].includes(key) && !shiftKey) {
      const target = event.target as HTMLElement;
      const thisButtonIndex = paletteButtons.indexOf(target);

      switch (key) {
        case 'ArrowDown':
          setFocusOnNextPaletteButton(thisButtonIndex);
          break;
        case 'ArrowUp':
          setFocusOnPreviousPaletteButton(thisButtonIndex);
          break;
      }
    }
  };

  const setFocusOnNextPaletteButton = (index: number) => {
    if (index < paletteButtons.length - 1) {
      paletteButtons[index + 1].focus();
    }
  };

  const setFocusOnPreviousPaletteButton = (index: number) => {
    if (index > 0) {
      paletteButtons[index - 1].focus();
    }
  };

  // Focus on the first focusable element in the most recently opened section (excluding the panel close button)
  const setFocusOnContent = () => {
    const openPalettes = document.querySelectorAll('.tpx-palette-section--selected');
    const interactiveElements =
      'button:not(.tpx-palette-close-button):not([disabled]), input, select, textarea';
    let firstFocusableElement: HTMLElement | null = null;

    if (openPalettes.length > 0) {
      firstFocusableElement =
        openPalettes[openPalettes.length - 1]?.querySelector(interactiveElements);
    }
    if (firstFocusableElement) {
      firstFocusableElement.focus();
    }
  };

  useEffect(() => {
    const handler = handleKeydown as EventListener;

    const timer = setTimeout(() => {
      paletteButtons.forEach(button => {
        button.addEventListener('keydown', handler);
      });
    }, 0);

    return () => {
      clearTimeout(timer);
      paletteButtons.forEach(button => {
        button.removeEventListener('keydown', handler);
      });
    };
  }, [children]);

  const toolPaletteClasses = classNames(
    'tpx-tool-palette',
    'flex',
    'flex-1',
    'relative',
    'box-border',
    'duration-standard',
    'sm:py-sm',
    'sm:transition-[margin]',
    'sm:ease-out',
    'lg:overflow-hidden ',
    'lg:ease-sidebar',
    'lg:transition-[width,background-color]',
    // Add the width and borders for the docked tool palette style
    toolPaletteStyle === 'docked'
      ? [
          'lg:w-[320rem]',
          'lg:border-r-[line-width:min(theme(borderWidth.themeBorderSize),10px)]',
          'lg:border-r-themeBorderColour',
          'sm:border-t-[line-width:min(theme(borderWidth.themeBorderSize),10px)]',
          'sm:border-t-themeBorderColour',
        ]
      : // Add the width for the floating tool palette style
        'lg:w-[360rem]',
    isOpen && 'sm:delay-[0.1s]' // Avoids the root menu transition being seen behind the opening section
  );

  const sectionsContainerClasses = classNames(
    'flex',
    'sm:flex-1',
    // Add before and after elements to position the buttons centrally on small screen
    // The before element doesn't shrink less than var(--smartDesignButtonSpace) to accommodate the smart design button
    'sm:before:flex-1',
    'sm:before:min-w-[var(--smartDesignButtonSpace)]',
    'sm:after:flex-1',
    'sm:overflow-x-auto sm:overflow-y-hidden',
    'lg:relative',
    'lg:flex-col',
    'lg:box-border',
    'lg:transition-[width]',
    'lg:duration-standard',
    toolPaletteStyle === 'docked' && (isOpen ? 'lg:w-[60rem]' : 'lg:w-full'),
    toolPaletteStyle === 'floating' && ['lg:w-[100rem]', 'lg:mr-[10rem]']
  );

  useEffect(() => {
    // Ensure focus mode is turned off if and when the tool palette gets reset
    if (ui.state.palette.section === null) {
      ui.dispatch(UserInterfaceActions.setViewOffset(0));
      ui.dispatch(UserInterfaceActions.setFocusMode(false));
      return;
    }
  }, [ui.state.palette.section]);

  // Set a value to account for the smart design button in the tailwind button width classes
  const smartDesignButtonSpace =
    experience.WIZARD.designAssistantType.designAssistantType === AssistantType.SmartDesign &&
    experience.EDITOR.toolPalette.showSmartDesignButton &&
    aiMode > AIMode.NotAvailable
      ? '50rem'
      : '0px';

  const buttonBarClasses = classNames(
    'flex',
    'sm:items-start',
    'lg:flex-col',
    'lg:overflow-auto',
    'p-xs',
    'shadow-sm',
    'lg:space-y-xxs',
    'lg:mr-[10rem]'
  );

  const handleTransitionEnd = (event: React.TransitionEvent<HTMLDivElement>) => {
    const targetClasses = (event.target as HTMLElement).classList;
    if (
      targetClasses.contains('tpx-tool-palette') ||
      targetClasses.contains('tpx-palette-section')
    ) {
      setFocusOnContent();
    }
  };

  return (
    <Theme
      id={'outer'}
      name={
        toolPaletteStyle === 'docked'
          ? isOpen
            ? 'tpx-theme-toolpaletteOpen'
            : 'tpx-theme-toolpaletteClosed'
          : undefined
      }
      tipLabel={isOpen ? 'Sidebar (Open)' : 'Sidebar (Closed)'}
      isRootTheme
      className={toolPaletteClasses}
      allowBorder={false}
      allowCorners={false}
      style={
        {
          '--smartDesignButtonSpace': smartDesignButtonSpace,
          '--availableWidth': 'calc(100vw - var(--smartDesignButtonSpace))',
          '--totalButtons': 4,
          '--mobilePaletteContentHeight':
            experience.EDITOR.toolPalette.mobileSlideUpPanels === 'msuc' ? 260 : 200,
        } as CSSProperties
      }
      ref={toolPaletteRef}
      onTransitionEnd={handleTransitionEnd}
    >
      <div id={'tool-palette-container'} className={sectionsContainerClasses} role="presentation">
        {toolPaletteStyle === 'floating' ? (
          <Theme
            name={'tpx-theme-toolpaletteClosed'}
            tipLabel={'Sidebar (Closed)'}
            isRootTheme
            id="tool-palette__group-two"
            className={buttonBarClasses}
            role="tablist"
            aria-orientation={ui.state.isSmallScreen ? 'horizontal' : 'vertical'}
          >
            {children}
          </Theme>
        ) : (
          <>{children}</>
        )}
      </div>
    </Theme>
  );
};

export default ToolPalette;
